/**
 * domains/learning/snapshotV2Core.ts
 *
 * Phase 4B -- Immutable Snapshot v2: client-side capture-body assembly (PURE).
 *
 * This module turns one already-computed SymbolContext (plus the active symbol
 * and the thesis/divergence read for that symbol) into the exact request body
 * the server's POST /api/learning/snapshot-v2/capture endpoint expects -- or a
 * typed "skip" when this evaluation must not be captured.
 *
 * DESIGN CONTRACT (read before changing):
 *   - PURE + type-only imports. At runtime this module imports NOTHING. The two
 *     value-free `import type` lines are erased by the compiler, so a bare-node
 *     / ts-node(transpileOnly) test can load it without dragging in the Zustand
 *     store, the thesis stores, the divergence runtime, or React. That is the
 *     whole reason core and runtime are split: the runtime wrapper
 *     (snapshotV2Runtime.ts) owns every heavy import; core stays loadable.
 *   - NEVER THROWS. buildCaptureBody wraps assembly in try/catch and degrades to
 *     { kind: 'skip', reason: 'error' } on any unexpected shape, so a malformed
 *     context can never break the selector path that calls it.
 *   - NEVER MUTATES ctx. It only reads fields off ctx and the deps; the returned
 *     body reuses the same RichSetup / thesis / divergence object references
 *     (they are serialized by postCaptureBody, never edited).
 *   - The SERVER is the sole capture authority. This module does NOT decide
 *     capture-worthiness (no T1-T8, no dedup, no throttle) and carries NO client
 *     dedupe in slice 1. It assembles a candidate; the server runs decideCapture
 *     + ON CONFLICT and returns captured | skipped | duplicate.
 *
 * SCOPE (slice 1): ACTIVE_SYMBOL only. The active-symbol guard is what keeps the
 * shared getSymbolContext seam from capturing the whole watchlist/portfolio
 * universe when this is reached from a multi-symbol loop.
 */

import type { RichSetup } from '../market/engines/setup2';
import type { StructuredThesis } from '../ai/thesis2/types';

/* --- local structural types (kept decoupled on purpose) ----------------- */

/**
 * Minimal view of SymbolContext that core actually reads. Declaring it locally
 * (rather than importing the full SymbolContext) keeps core from coupling to the
 * market service's type surface; the runtime wrapper passes the real context,
 * which structurally satisfies this.
 */
export interface CtxForCaptureV2 {
  symbol: string;
  interval: string;
  primaryRichSetup?: RichSetup | null;
  // A-iii: reference-price inputs (read-only; present on the full SymbolContext).
  curPrice?: number;
  candles?: ReadonlyArray<{ ts: number; close: number }>;
  dataQuality?: { hasRealData?: boolean };
}

/**
 * Minimal view of DivergenceResult that core reads (only `level`). The runtime
 * wrapper passes the full DivergenceResult, which is assignable to this and is
 * carried through to the server untouched. Declared locally so core does not
 * depend on the divergence module's exact type-export path.
 */
export interface DivergenceLike {
  level: string | null;
}

/**
 * The CaptureInput primitives, mirroring the server's CaptureInput contract
 * (server/learning/captureDecision.js). Field names are LOCKED to match the
 * server exactly so the assembled body passes validation + the 7 consistency
 * checks by construction. Optional thesis/divergence fields are present ONLY
 * when those subsystems ran for this evaluation; absence (undefined) suppresses
 * the corresponding server-side trigger.
 */
export interface CaptureInputV2 {
  symbol: string;
  timeframe: string;
  inputHash: string;
  classification: string;
  state: string;
  isActionable: boolean;
  setupScore: number;
  readinessScore: number;
  direction?: string | null;
  isNVS: boolean;
  nvsReasonKey?: string | null;
  thesisChanged?: boolean;
  thesisState?: string | null;
  actionPosture?: string | null;
  bias?: string | null;
  divergenceLevel?: string | null;
  atMs: number;
}

/**
 * A-iii: optional reference-price provenance block. Evidence-only; the server
 * (snapshotV2Routes.normalizeReference) validates it structurally and maps it to
 * the four reference_* columns. NEVER enters input / capture_fingerprint /
 * decision_hash / snapshot_id / dedup. `source` is the fixed provenance label
 * (the only value the server allows); the data-provider lineage feeds
 * `confidence` only, never `source`.
 */
export interface ReferenceBlock {
  price: number;
  source: 'SYMBOL_CONTEXT_LAST_CANDLE_CLOSE';
  at: number;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

/** The exact request body POSTed to /api/learning/snapshot-v2/capture. */
export interface CaptureBody {
  input: CaptureInputV2;
  richSetup: RichSetup;
  structuredThesis: StructuredThesis | null;
  divergence: DivergenceLike | null;
  universeRole: 'ACTIVE_SYMBOL';
  reference?: ReferenceBlock;
}

/** Result of assembly: either a typed skip (with reason) or a ready-to-POST body. */
export type CaptureBodyResult =
  | { kind: 'skip'; reason: string }
  | { kind: 'post'; body: CaptureBody };

/** Inputs the runtime wrapper resolves from the heavy stores and passes in. */
export interface BuildCaptureDeps {
  activeSymbol: string | null;
  structuredThesis: StructuredThesis | null;
  divergence: DivergenceLike | null;
  nowMs: number;
}

/* --- helpers ------------------------------------------------------------ */

/**
 * Deterministic NVS reason key from the RichSetup's fatal disqualifier ids.
 * Sorted + joined so the same disqualifier set always yields the same key
 * (the server's T6 NVS trigger compares this across evaluations). For the base
 * NVS sentinel this collapses to 'NO_VALID_SETUP', which is an accepted slice-1
 * simplification. Returns 'UNSPECIFIED' when there are no usable ids.
 */
export function deriveNvsReasonKey(blockingDisqs: ReadonlyArray<string> | null | undefined): string {
  const arr = Array.isArray(blockingDisqs)
    ? blockingDisqs.filter((x) => typeof x === 'string' && x.length > 0)
    : [];
  if (arr.length === 0) return 'UNSPECIFIED';
  return [...arr].sort().join('|');
}

/* --- reference-price block (A-iii, pure) -------------------------------- */

/**
 * Defensive epoch-ms normalization. Live ThetaData/Polygon `ts` are already
 * epoch ms; a value that looks like epoch SECONDS (0 < ts < 1e12) is scaled to
 * ms. The threshold cleanly separates 2026 seconds (~1.7e9) from ms (~1.7e12).
 */
export function toEpochMs(ts: number): number {
  return ts > 0 && ts < 1e12 ? ts * 1000 : ts;
}

/**
 * Parse a bar-interval label to milliseconds. Handles '1min'/'5min'/'15min'
 * and 'Nm'/'Nh'/'ND' forms; unparseable input falls back to 5 minutes. The
 * result only gates the HIGH<->MEDIUM freshness boundary, never price/at, so the
 * fallback can never null a reference or change its value.
 */
export function parseIntervalToMs(interval: string | null | undefined): number {
  const FALLBACK = 5 * 60 * 1000;
  if (typeof interval !== 'string') return FALLBACK;
  const m = interval.trim().toLowerCase().match(/^(\d+)\s*(min|m|h|hr|d)$/);
  if (!m) return FALLBACK;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n) || n <= 0) return FALLBACK;
  const unit = m[2];
  const unitMs =
    unit === 'h' || unit === 'hr' ? 60 * 60 * 1000 :
    unit === 'd'                  ? 24 * 60 * 60 * 1000 :
                                    60 * 1000; // 'min' / 'm'
  return n * unitMs;
}

/** Freshness window = FRESH_MULTIPLIER bars (D3 = 2). */
const FRESH_MULTIPLIER = 2;

/**
 * Confidence tier for the reference price (A-iii, locked rule):
 *   HIGH   = live (api) source AND latest candle fresh
 *   MEDIUM = live (api) source AND latest candle stale
 *   LOW    = non-live source (csv / sample / unknown / absent)
 *
 * "Live" is read by the caller from ctx.dataQuality.hasRealData (=== source
 * 'api'). Freshness is (nowMs - at) <= FRESH_MULTIPLIER * intervalMs. The known
 * EDT timestamp skew makes `at` sit ~1h AHEAD of now (negative age), which is
 * correctly treated as fresh here and absorbed by the server's 2h future
 * tolerance -- NOT fixed in A-iii.
 */
export function computeReferenceConfidence(
  isLive: boolean,
  nowMs: number,
  atMs: number,
  interval: string | null | undefined,
): 'HIGH' | 'MEDIUM' | 'LOW' {
  if (!isLive) return 'LOW';
  const ageMs = nowMs - atMs;
  const fresh = ageMs <= FRESH_MULTIPLIER * parseIntervalToMs(interval);
  return fresh ? 'HIGH' : 'MEDIUM';
}

/**
 * Build the optional reference-price block from one SymbolContext, or null.
 *
 *   price = ctx.curPrice (= candles[last].close, engine-aligned)
 *   at    = toEpochMs(candles[last].ts)
 *   source= 'SYMBOL_CONTEXT_LAST_CANDLE_CLOSE' (fixed provenance label)
 *   conf  = computeReferenceConfidence(hasRealData, nowMs, at, interval)
 *
 * Returns null -- meaning NO `reference` key on the body, capture unaffected --
 * on any missing/malformed shape. NEVER throws; NEVER fabricates a price or a
 * timestamp. Pure: `nowMs` is injected (the same Date.now() that becomes
 * input.atMs), so it is unit-testable with no clock dependency.
 */
export function buildReferenceBlock(ctx: CtxForCaptureV2, nowMs: number): ReferenceBlock | null {
  try {
    const candles = ctx.candles;
    if (!Array.isArray(candles) || candles.length === 0) return null;

    const last = candles[candles.length - 1];
    if (!last) return null;

    const price = ctx.curPrice;
    if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) return null;

    const rawTs = last.ts;
    if (typeof rawTs !== 'number' || !Number.isFinite(rawTs)) return null;
    const at = toEpochMs(rawTs);

    const isLive = !!(ctx.dataQuality && ctx.dataQuality.hasRealData === true);
    const confidence = computeReferenceConfidence(isLive, nowMs, at, ctx.interval);

    return {
      price: price,
      source: 'SYMBOL_CONTEXT_LAST_CANDLE_CLOSE',
      at: at,
      confidence: confidence,
    };
  } catch (_e) {
    // Defensive: a malformed context must never throw into the selector path.
    return null;
  }
}

/* --- assembly (pure) ---------------------------------------------------- */

/**
 * Assemble the capture body for one evaluation, or return a typed skip.
 *
 * Guards, in order:
 *   1. active-symbol guard -- skip 'not-active-symbol' unless ctx.symbol matches
 *      deps.activeSymbol (case-normalized). This is what scopes slice 1 to the
 *      active symbol even though getSymbolContext is a shared selector.
 *   2. primary guard -- skip 'no-primary-rich-setup' when there is no evaluated
 *      RichSetup (unevaluated symbol: primaryRichSetup is null/absent). Note an
 *      *evaluated* NVS is a real RichSetup sentinel and is NOT skipped here.
 *
 * Then it maps the RichSetup canonical into CaptureInput, pulling every
 * consistency-checked field from the same RichSetup the body carries (so the
 * server's 7 input<->richSetup consistency checks hold by construction), adds
 * thesis fields only when a thesis was read, divergence level only when
 * divergence was computed, and stamps universeRole = 'ACTIVE_SYMBOL'.
 *
 * Never throws: any unexpected shape degrades to { kind: 'skip', reason: 'error' }.
 */
export function buildCaptureBody(ctx: CtxForCaptureV2, deps: BuildCaptureDeps): CaptureBodyResult {
  try {
    // (1) active-symbol guard (case-normalized; null active => never matches)
    const sym = (ctx && ctx.symbol ? ctx.symbol : '').toUpperCase();
    const active = (deps && deps.activeSymbol ? deps.activeSymbol : '').toUpperCase();
    if (!sym || sym !== active) {
      return { kind: 'skip', reason: 'not-active-symbol' };
    }

    // (2) primary RichSetup guard (unevaluated symbol -> nothing to capture)
    const primary = ctx.primaryRichSetup;
    if (!primary) {
      return { kind: 'skip', reason: 'no-primary-rich-setup' };
    }

    const isNVS = primary.setup_classification === 'NO_VALID_SETUP';
    const st = deps.structuredThesis;
    const div = deps.divergence;

    const input: CaptureInputV2 = {
      symbol: ctx.symbol,
      timeframe: ctx.interval,
      inputHash: primary.audit.input_hash,
      classification: primary.setup_classification,
      state: primary.setup_state,
      isActionable: primary.actionability.is_actionable,
      setupScore: primary.setup_score,
      readinessScore: primary.readiness_score,
      direction: primary.direction,
      isNVS: isNVS,
      atMs: deps.nowMs,
    };

    // NVS reason key only when this is an NVS evaluation.
    if (isNVS) {
      input.nvsReasonKey = deriveNvsReasonKey(primary.actionability.blocking_disqs);
    }

    // Thesis fields only when thesis2 ran for this symbol this cycle.
    if (st) {
      if (st.change_detection) {
        input.thesisChanged = st.change_detection.changed;
      }
      input.thesisState = st.thesis_state;
      input.actionPosture = st.action_posture;
      input.bias = st.bias;
    }

    // Divergence level only when divergence was computed this cycle.
    if (div) {
      input.divergenceLevel = div.level;
    }

    const body: CaptureBody = {
      input: input,
      richSetup: primary,
      structuredThesis: st ? st : null,
      divergence: div ? div : null,
      universeRole: 'ACTIVE_SYMBOL',
    };

    // A-iii: attach the optional reference-price block (evidence-only). It is
    // computed from ctx + nowMs and never touches `input`, so the server's
    // capture_fingerprint / decision_hash / snapshot_id / dedup are unaffected.
    // Omitted entirely when the helper returns null (no candle / malformed).
    const reference = buildReferenceBlock(ctx, deps.nowMs);
    if (reference) {
      body.reference = reference;
    }

    return { kind: 'post', body: body };
  } catch (_e) {
    // Defensive: malformed context must never throw into the selector path.
    return { kind: 'skip', reason: 'error' };
  }
}

/* --- transport (fire-and-forget) --------------------------------------- */

/**
 * Fire-and-forget POST of an assembled body to the capture endpoint. Uses the
 * global fetch + window.location.origin, with a localhost:3000 fallback for
 * non-browser contexts (mirrors the existing researchSnapshot POST convention).
 * Same-origin, so the session cookie rides along with no explicit credentials.
 *
 * Returns void and swallows every error: it does not await the response, does
 * not surface failures, and never throws into its caller. There is deliberately
 * NO client dedupe here in slice 1 -- the server is the sole dedup authority.
 */
export function postCaptureBody(body: CaptureBody): void {
  try {
    const f: typeof fetch | null = typeof fetch !== 'undefined' ? fetch : null;
    if (!f) return;

    const hasWindow =
      typeof window !== 'undefined' && !!window.location && !!window.location.host;
    const origin = hasWindow
      ? `${window.location.protocol}//${window.location.host}`
      : 'http://localhost:3000';

    // Not awaited: fire-and-forget. Both sync and async failures are swallowed.
    Promise.resolve(
      f(`${origin}/api/learning/snapshot-v2/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
    )
      .then(() => undefined)
      .catch(() => undefined);
  } catch (_e) {
    /* never throw into the caller */
  }
}
